import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Client-side, localStorage-only wishlist. No backend calls.
 * @typedef {{ id: string, handle: string }} WishlistEntry
 */
export class Wishlist {
  static #STORAGE_KEY = 'shopify_wishlist';

  /**
   * @returns {WishlistEntry[]}
   */
  static getProducts() {
    try {
      const raw = localStorage.getItem(this.#STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /**
   * @param {string} id
   */
  static has(id) {
    return this.getProducts().some((entry) => entry.id === String(id));
  }

  /**
   * Toggles a product in/out of the wishlist and dispatches `WishlistUpdateEvent` on document.
   * @param {string} id
   * @param {string} handle
   * @returns {boolean} Whether the product ended up in the wishlist.
   */
  static toggle(id, handle) {
    const productId = String(id);
    const products = this.getProducts();
    const index = products.findIndex((entry) => entry.id === productId);
    const added = index === -1;

    if (added) {
      products.push({ id: productId, handle });
    } else {
      products.splice(index, 1);
    }

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
    } catch {
      // localStorage unavailable (private mode, quota, disabled) - continue without persistence.
    }

    document.dispatchEvent(
      new CustomEvent('WishlistUpdateEvent', {
        bubbles: true,
        detail: { productId, added },
      })
    );

    return added;
  }

  /**
   * Removes a product from the wishlist (no-op if it isn't saved). Used by the wishlist
   * drawer's remove control, where only the id is known/needed.
   * @param {string} id
   */
  static remove(id) {
    const productId = String(id);
    const products = this.getProducts();
    const index = products.findIndex((entry) => entry.id === productId);

    if (index === -1) return;

    products.splice(index, 1);

    try {
      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(products));
    } catch {
      // localStorage unavailable (private mode, quota, disabled) - continue without persistence.
    }

    document.dispatchEvent(
      new CustomEvent('WishlistUpdateEvent', {
        bubbles: true,
        detail: { productId, added: false },
      })
    );
  }
}

/**
 * @typedef {{ wishlistToggleButton: HTMLButtonElement }} WishlistButtonRefs
 * @extends {Component<WishlistButtonRefs>}
 */
class WishlistButton extends Component {
  requiredRefs = ['wishlistToggleButton'];

  connectedCallback() {
    super.connectedCallback();
    this.#syncState();
    document.addEventListener('WishlistUpdateEvent', this.#handleWishlistUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('WishlistUpdateEvent', this.#handleWishlistUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#syncState();
  }

  /** @param {Event} event */
  toggle(event) {
    // Wishlist buttons can be nested inside a card's full-bleed <a> (e.g. the homepage
    // featured-products block) - stop the click from also navigating to the product.
    event.preventDefault();
    event.stopPropagation();

    const added = Wishlist.toggle(this.dataset.productId ?? '', this.dataset.productHandle ?? '');
    this.#setState(added);
  }

  /** @param {CustomEvent<{ productId: string, added: boolean }>} event */
  #handleWishlistUpdate = (event) => {
    if (event.detail.productId !== this.dataset.productId) return;
    this.#setState(event.detail.added);
  };

  #syncState() {
    this.#setState(Wishlist.has(this.dataset.productId ?? ''));
  }

  /** @param {boolean} active */
  #setState(active) {
    this.classList.toggle('is-wishlisted', active);

    const button = this.refs.wishlistToggleButton;
    if (!button) return;

    button.setAttribute('aria-pressed', String(active));
    const label = active ? button.dataset.labelRemove : button.dataset.labelAdd;
    if (label) button.setAttribute('aria-label', label);
  }
}

if (!customElements.get('wishlist-button')) {
  customElements.define('wishlist-button', WishlistButton);
}

/**
 * Displays the wishlist count via a bubble, formatted the same way as `assets/cart-icon.js`'s
 * `CartIcon` (same class toggles, same pop-in animation, same >=100 blank-out behavior).
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wishlistBubble - The wishlist bubble element.
 * @property {HTMLElement} wishlistBubbleText - The wishlist bubble text element.
 * @property {HTMLElement} wishlistBubbleCount - The wishlist bubble count element.
 *
 * @extends {Component<Refs>}
 */
class WishlistCount extends Component {
  requiredRefs = ['wishlistBubble', 'wishlistBubbleText', 'wishlistBubbleCount'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('WishlistUpdateEvent', this.#handleWishlistUpdate);
    // Unlike the cart count, the wishlist count is always synchronously available from
    // localStorage, at zero cost - so unlike cart-icon.js's delta-based updates (which trust
    // an itemCount *added* and add it to whatever's currently displayed), every render here
    // re-reads the full, authoritative count from Wishlist.getProducts() directly. No
    // incremental state to drift out of sync, ever.
    this.renderWishlistBubble(false);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('WishlistUpdateEvent', this.#handleWishlistUpdate);
  }

  updatedCallback() {
    super.updatedCallback();
    // sections/header.liquid re-fetches and morphs the header shortly after every page load
    // (see @theme/section-hydration). The freshly-fetched copy is server-rendered Liquid, which
    // can never know the localStorage wishlist state, so it always comes back empty/hidden -
    // morph() then overwrites whatever this component had just rendered. Re-sync from the real
    // source of truth every time this component is patched by a Section Rendering API update,
    // exactly like WishlistButton's own updatedCallback already does.
    this.renderWishlistBubble(false);
  }

  #handleWishlistUpdate = () => {
    this.renderWishlistBubble();
  };

  /**
   * @param {boolean} [animate]
   */
  renderWishlistBubble = async (animate = true) => {
    const itemCount = Wishlist.getProducts().length;

    this.refs.wishlistBubbleCount.classList.toggle('hidden', itemCount === 0);
    this.refs.wishlistBubble.classList.toggle('visually-hidden', itemCount === 0);
    this.refs.wishlistBubble.classList.toggle('cart-bubble--animating', itemCount > 0 && animate);

    this.refs.wishlistBubbleCount.textContent = itemCount < 100 ? String(itemCount) : '';

    this.classList.toggle('header-actions__wishlist-icon--has-items', itemCount > 0);

    if (!animate) return;
    await onAnimationEnd(this.refs.wishlistBubbleText);

    this.refs.wishlistBubble.classList.remove('cart-bubble--animating');
  };
}

if (!customElements.get('wishlist-count')) {
  customElements.define('wishlist-count', WishlistCount);
}
